import { useState, useEffect, useRef, useCallback } from "react";
import { api, getWsToken, buildWsUrl, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { offboardCapabilityBlocked } from "@/lib/commandGating";
import {
  REAL_STREAM_HZ, REAL_ACTIVE_HOLD_MS, PRECHECK_REQUIRED,
  mapAxes, targetFromDirections, zeroTarget, isRealDirection,
  buildTakeoffStart, buildManualStop, buildVelocityFrame,
  realEnabledFromStatus, movementAllowed, authorityLostDuringActive, authoritativeSafety,
  canStartFlight, holdLatchFrom, canRearmMovement, AUTHORITY_STATES, validateControlLimits, profileControlLimits, validateTakeoffPolicy, validateTakeoffAltitude,
  nextSpeedProfile, prevSpeedProfile, DEFAULT_SPEED_PROFILE,
  newClientCommandId, postCriticalCommand, fetchCommand,
  isCriticalFailStatus, isUncertainStatus, canStartCritical, criticalUnlockReady,
} from "@/lib/offboardControl";

// LOT — State machine WS/deadman/lease OFFBOARD réelle, PARTAGÉE (unique source de vérité).
// UNIFIED.7 : utilisée par ManualControl, le cockpit physique UNIQUE. Aucune duplication.
// Reprend fidèlement le chemin ARM -> LOITER -> AUTO OFFBOARD validé.
//
// options:
//   enabled     : si false, ne se connecte pas et n'attache aucun listener (mode simulation).
//   trace       : { collector, isMeasuring } — instrumentation A5 optionnelle.

// Modes PX4 confirmant la sortie native DEMANDÉE (R2 : LAND <-> LAND/AUTO.LAND, RTL <-> RTL/AUTO.RTL).
const NATIVE_EXIT_MODES = { land: ["LAND", "AUTO.LAND"], rtl: ["RTL", "AUTO.RTL"] };

// WEBXR.REAL.CONTROL.1 — stampIntent : chaque trame NON NULLE porte intent_ts_ms (époque de la dernière intention
// analogique), contrôlé côté Backend (stale_intent). Les trames zéro (keepalive/deadman) ne sont jamais horodatées.
export function useRealOffboardControl(droneId, { enabled = true, trace = null, flightController = null, stampIntent = false } = {}) {
  const [conn, setConn] = useState("idle");        // idle|connecting|open|closed
  const [status, setStatus] = useState(null);      // dernier offboard_status source=raspberry
  const [granted, setGranted] = useState(null);    // control_granted
  const [transport, setTransport] = useState(null);
  const [phase, setPhase] = useState("idle");
  const [precheck, setPrecheck] = useState(0);
  const [orch, setOrch] = useState(null);
  const [activeDirs, setActiveDirs] = useState(new Set());
  const [criticalAction, setCriticalAction] = useState(null); // {type,commandId,status,uncertain,note}
  const [banner, setBanner] = useState(null);
  const [preflight, setPreflight] = useState(null); // UNIFIED.2 — précheck sol autoritaire backend
  const [speedProfile, setSpeedProfile] = useState(DEFAULT_SPEED_PROFILE); // UNIFIED.3 — slow|normal|rapid
  const [backendTakeoffPolicy, setBackendTakeoffPolicy] = useState(null);  // UNIFIED.3 — politique TAKEOFF backend
  const [lastError, setLastError] = useState(null); // WEBXR.REAL.CONTROL.1 — dernier refus explicite {error, at}
  const analogAtRef = useRef(0);

  const wsRef = useRef(null);
  const seqRef = useRef(0);
  const sidRef = useRef(null);
  const targetRef = useRef({ vx: 0, vy: 0, vz: 0, yaw_rate: 0 });
  const streamRef = useRef(null);
  const streamingRef = useRef(false);
  const activeAtRef = useRef(null);
  const phaseRef = useRef("idle");
  const moveAllowedRef = useRef(false);
  const pollRef = useRef(null);
  const pollTickRef = useRef(false);
  const critRef = useRef(null);       // miroir de criticalAction (accès synchrone dans les handlers)
  const critPollRef = useRef(null);   // intervalle de suivi read-only 1 Hz
  // A5 — suivi d'intention opérateur pour l'instrumentation latence.
  const inputEventIdRef = useRef(0);
  const inputKindRef = useRef("zero_keepalive");
  const curDirRef = useRef(null);
  const inputChangedRef = useRef({ perf: 0, epoch: 0 });
  // Multi-axes : ensemble de directions maintenues (boutons/clavier) + contribution analogique (gamepad).
  const heldDirsRef = useRef(new Set());
  const analogRef = useRef({ pitch: 0, roll: 0, lift: 0, yaw: 0, active: false }); // UNIFIED.3 : entrée NORMALISÉE
  const profileLimitsRef = useRef(null);  // limites du profil courant (cible opérateur)
  const agentLimitsRef = useRef(null);    // enveloppe max Agent (clamp final frontend)
  const holdLatchRef = useRef(holdLatchFrom()); // HOLD.CONTINUITY.1 — entrées tenues à relâcher avant reprise
  const lastAgentStateRef = useRef(null);
  const nativeExitRef = useRef(false);
  // PILOT.BLOCKERS.1 — réautorisation contrôlée après safetyZero / action critique.
  const rearmPendingRef = useRef(false);
  const rearmAtStatusRef = useRef(0);   // n° du dernier statut vu au verrouillage / retour cockpit
  const statusCountRef = useRef(0);
  const focusedRef = useRef(true);
  const orchRef = useRef(null);

  useEffect(() => { phaseRef.current = phase; }, [phase]);

  // UNIFIED.3 — limites de contrôle AUTORITAIRES Agent + profil de vitesse.
  const controlLimits = validateControlLimits(status?.control_limits);      // {max_xy,max_z,max_yaw,max_yaw_deg}|null
  const controlLimitsReady = !!controlLimits;
  const profileLimits = profileControlLimits(controlLimits, speedProfile);  // limites profil|null
  const agentTakeoffPolicy = validateTakeoffPolicy(status?.takeoff_policy);
  const takeoffPolicyMismatch = !!(backendTakeoffPolicy && agentTakeoffPolicy
    && (backendTakeoffPolicy.min !== agentTakeoffPolicy.min || backendTakeoffPolicy.max !== agentTakeoffPolicy.max));
  // UNIFIED.3A — les DEUX sources (Backend + Agent) sont obligatoires ET doivent coïncider (fail-closed).
  const takeoffPolicyReady = !!(backendTakeoffPolicy && agentTakeoffPolicy && !takeoffPolicyMismatch);
  const takeoffPolicy = takeoffPolicyReady ? agentTakeoffPolicy : null;
  useEffect(() => { profileLimitsRef.current = profileLimits; agentLimitsRef.current = controlLimits; });
  // UNIFIED.3A — perte des limites Agent EN SESSION => zéro immédiat (fail-closed). Le stream peut continuer
  // à envoyer des trames zéro (PRESTREAM/deadman) ; aucune nouvelle consigne non nulle. Pas de LAND/RTL auto.
  const prevLimitsRef = useRef(false);
  useEffect(() => {
    if (prevLimitsRef.current && !controlLimitsReady) {
      heldDirsRef.current.clear();
      analogRef.current = { pitch: 0, roll: 0, lift: 0, yaw: 0, active: false };
      targetRef.current = zeroTarget();
      setActiveDirs(new Set());
      moveAllowedRef.current = false;
      setBanner("Limites Agent perdues — mouvement remis à zéro");
    }
    prevLimitsRef.current = controlLimitsReady;
  }, [controlLimitsReady]);

  const realEnabled = realEnabledFromStatus(status);
  const pc = status?.preconditions || {};
  const entryReady = status?.source === "raspberry" && status?.state === "READY"
    && status?.px4_mode === "POSCTL" && pc.scope === "entry" && pc.allowed === true
    && Array.isArray(pc.reasons) && pc.reasons.length === 0;
  const isActive = phase === "active";
  // Gate runtime fail-closed = fonction PURE testée (exigence 8) + gate autoritatif backend + verrou action critique.
  const moveAllowed = movementAllowed({
    wsOpen: conn === "open",
    leaseGranted: !!(granted && granted.session_id),
    status,
    phase,
    activeHoldDone: moveAllowedRef.current,
    orchMovementAllowed: orch?.movement_allowed === true,
  }) && !criticalAction && controlLimitsReady;

  // UNIFIED.2 — gate PUR fail-closed du départ au sol (ARM+TAKEOFF). N'utilise PAS entryReady.
  const canStartFlightNow = canStartFlight({
    conn,
    sessionId: granted?.session_id,
    gatewayForwarded: transport?.gateway_forwarded,
    realEnabled,
    source: status?.source,
    dryRun: status?.dry_run,
    preflightAllowed: preflight?.allowed,
    fcBlocked: offboardCapabilityBlocked(flightController),
    criticalAction,
    phase,
    armed: authoritativeSafety(status).armed,
    landedState: authoritativeSafety(status).landed_state,
  }) && controlLimitsReady && takeoffPolicyReady;

  const send = useCallback((obj) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) { ws.send(JSON.stringify(obj)); return true; }
    return false;
  }, []);

  const zeroLocal = useCallback(() => {
    heldDirsRef.current.clear();
    analogRef.current = { pitch: 0, roll: 0, lift: 0, yaw: 0, active: false };
    targetRef.current = zeroTarget();
    setActiveDirs(new Set());
  }, []);

  // Recalcule la cible 4 axes = boutons (limites profil) + analogique (limites profil) surchargeant PAR AXE.
  // Utilise les limites du PROFIL courant (changement de vitesse immédiat même stick maintenu).
  const recomputeTarget = useCallback(() => {
    const L = profileLimitsRef.current;
    const btn = targetFromDirections(heldDirsRef.current, L);
    const n = analogRef.current;
    const a = n.active ? mapAxes({ pitch: n.pitch, roll: n.roll, lift: n.lift, yaw: n.yaw }, L)
                       : { vx: 0, vy: 0, vz: 0, yaw_rate: 0 };
    targetRef.current = {
      vx: n.active && a.vx !== 0 ? a.vx : btn.vx,
      vy: n.active && a.vy !== 0 ? a.vy : btn.vy,
      vz: n.active && a.vz !== 0 ? a.vz : btn.vz,
      yaw_rate: n.active && a.yaw_rate !== 0 ? a.yaw_rate : btn.yaw_rate,
    };
  }, []);

  // Flux de consignes à 10 Hz — alimente le deadman pendant PRESTREAM/REQUESTING/ACTIVE.
  const startStream = useCallback(() => {
    if (streamingRef.current) return;
    streamingRef.current = true;
    streamRef.current = setInterval(() => {
      if (!sidRef.current) return;
      seqRef.current += 1;
      const t = targetRef.current;
      const seq = seqRef.current;
      // Trame runtime 4 axes clampée indépendamment via buildVelocityFrame à l'enveloppe max AGENT (défense en profondeur).
      const obj = buildVelocityFrame({ session_id: sidRef.current, seq, vx: t.vx, vy: t.vy, vz: t.vz, yaw_rate: t.yaw_rate }, agentLimitsRef.current);
      if (stampIntent && (obj.vx || obj.vy || obj.vz || obj.yaw_rate)) obj.intent_ts_ms = analogAtRef.current;
      const traceOn = !!(trace?.collector && trace.collector.velocityTracingEnabled());
      let sentInfo = null;
      if (traceOn) {
        const perf = performance.now();
        const epoch = performance.timeOrigin + perf;
        const kind = inputKindRef.current;
        const ch = inputChangedRef.current;
        obj.trace = { v: 1, input_event_id: inputEventIdRef.current, input_kind: kind,
                      fe_send_epoch_ms: epoch, fe_send_perf_ms: perf,
                      input_changed_epoch_ms: ch.epoch, input_changed_perf_ms: ch.perf };
        sentInfo = { feEpoch: epoch, fePerf: perf, inputEventId: inputEventIdRef.current, inputKind: kind,
                     changedEpoch: ch.epoch, changedPerf: ch.perf };
      }
      const ok = send(obj);
      if (traceOn && ok) {
        trace.collector.recordSent(seq, sentInfo);
        if (sentInfo.inputKind === "release") inputKindRef.current = "zero_keepalive";
      }
    }, Math.round(1000 / REAL_STREAM_HZ));
  }, [send, trace, stampIntent]);

  const stopStream = useCallback(() => {
    streamingRef.current = false;
    if (streamRef.current) { clearInterval(streamRef.current); streamRef.current = null; }
  }, []);

  const startStatusPoll = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(() => {
      const p = phaseRef.current;
      if (!sidRef.current || ["idle", "gateway_connecting", "link_lost", "takeover"].includes(p)) return;
      if (p !== "granted") { pollTickRef.current = !pollTickRef.current; if (!pollTickRef.current) return; }
      const obj = { type: "manual_status", session_id: sidRef.current };
      if (trace?.isMeasuring?.()) {
        const perf = performance.now();
        const epoch = performance.timeOrigin + perf;
        obj.trace = { v: 1, probe: true, input_kind: "probe",
                      t1_perf_ms: perf, t1_epoch_ms: epoch,
                      fe_send_epoch_ms: epoch, fe_send_perf_ms: perf };
      }
      send(obj);
    }, 250);
  }, [send, trace]);
  const stopStatusPoll = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  // HOLD.CONTINUITY.1 — cible zéro + verrou neutre (aucun ancien bouton/axe ne relance un déplacement).
  const latchAndZero = useCallback(() => {
    const prev = holdLatchRef.current;
    holdLatchRef.current = holdLatchFrom([...prev.dirs, ...heldDirsRef.current], prev.analog || analogRef.current.active);
    curDirRef.current = null; inputKindRef.current = "zero_keepalive";
    zeroLocal();
  }, [zeroLocal]);

  const armRearm = useCallback(() => {
    moveAllowedRef.current = false;
    rearmPendingRef.current = true;
    rearmAtStatusRef.current = statusCountRef.current;
  }, []);

  // Remise à zéro locale immédiate + manual_stop best-effort (sécurités navigateur).
  // PILOT.BLOCKERS.1 : verrou RÉVERSIBLE (armRearm) ; relâchements perdus -> aucun verrou de touche persistant.
  const safetyZero = useCallback((reason, { focusLost = false } = {}) => {
    latchAndZero();
    holdLatchRef.current = holdLatchFrom([], holdLatchRef.current.analog);
    if (focusLost) focusedRef.current = false;
    armRearm();
    if (sidRef.current) send(buildManualStop(sidRef.current));
    if (reason) setBanner(reason);
  }, [send, latchAndZero, armRearm]);

  const connect = useCallback(async () => {
    setConn("connecting");
    try {
      const token = await getWsToken();
      const ws = new WebSocket(buildWsUrl(`/api/ws/offboard/${droneId}`, token));
      wsRef.current = ws;
      // A1 — seule la connexion COURANTE agit sur l'état (anciennes connexions ignorées).
      ws.onopen = () => { if (wsRef.current === ws) setConn("open"); };
      ws.onclose = () => {
        // WEBXR.REAL.CONTROL.1 A1 — une ANCIENNE connexion déjà remplacée ne touche jamais la nouvelle.
        if (wsRef.current !== null && wsRef.current !== ws) return;
        setConn("closed"); stopStream(); stopStatusPoll(); moveAllowedRef.current = false;
        setPreflight(null);
        if (phaseRef.current === "link_lost") return;  // motif déjà affiché (perte d'autorité explicite)
        if (phaseRef.current !== "idle" && phaseRef.current !== "exiting") {
          setPhase("link_lost"); setBanner("Liaison WebSocket perdue — contrôle désactivé");
        }
      };
      ws.onerror = () => { if (wsRef.current === ws) setConn("closed"); };
      ws.onmessage = (ev) => {
        if (wsRef.current !== ws) return;
        let m; try { m = JSON.parse(ev.data); } catch { return; }
        if (m.type === "control_granted") {
          setGranted(m); sidRef.current = m.session_id; setPhase("gateway_connecting");
          holdLatchRef.current = holdLatchFrom(); lastAgentStateRef.current = null; nativeExitRef.current = false;
          setPrecheck(0); setPreflight(null); setBackendTakeoffPolicy(null); setSpeedProfile(DEFAULT_SPEED_PROFILE);  // UNIFIED.2/3 — nouvelle lease => reset fail-closed + profil LENT
        } else if (m.type === "offboard_transport") {
          setTransport(m);
          if (m.transport === "gateway_connected" && m.gateway_forwarded === true) {
            if (phaseRef.current === "gateway_connecting") setPhase("granted");
            startStatusPoll();
          }
        } else if (m.type === "control_denied") {
          stopStream(); stopStatusPoll(); zeroLocal(); moveAllowedRef.current = false; setPreflight(null);
          const msg = m.reason === "real_control_disabled" ? "Contrôle réel désactivé par configuration serveur"
            : m.reason === "admin_required" ? "Réservé aux administrateurs (contrôle réel)"
            : m.reason === "already_controlled" ? "Drone déjà contrôlé par une autre session"
            : String(m.reason || "").startsWith("lease_") ? `Autorité de pilotage indisponible (${m.reason})`
            : `Contrôle refusé: ${m.reason || ""}`;
          setPhase("link_lost"); setBanner(msg); setLastError({ error: m.reason || "control_denied", at: Date.now() });
        } else if (m.type === "orchestration_precheck") {
          // UNIFIED.2 — précheck sol READ-ONLY backend. Accepté UNIQUEMENT s'il correspond à la lease courante.
          if (m.session_id == null || m.session_id === sidRef.current) {
            setPreflight({ allowed: m.allowed === true, reasons: m.reasons || [], checks: m.checks || {} });
            if (m.takeoff_policy) setBackendTakeoffPolicy(validateTakeoffPolicy(m.takeoff_policy));  // UNIFIED.3
          }
        } else if (m.type === "offboard_status" && m.source === "raspberry") {
          setStatus(m);
          statusCountRef.current += 1;
          // HOLD.CONTINUITY.1 — entrée HOLDING : intention effacée, flux neutre 10 Hz MAINTENU (pas de stopStream).
          if (m.state === "HOLDING" && lastAgentStateRef.current !== "HOLDING"
              && ["active", "active_hold"].includes(phaseRef.current)) {
            latchAndZero();
            setBanner("Maintien de position — relâchez puis reprenez les commandes");
          }
          // R1 — sortie native LAND/RTL en cours : commandes verrouillées, flux neutre et handoff conservés.
          if (m.native_exit_pending === true && !nativeExitRef.current
              && ["active", "active_hold"].includes(phaseRef.current)) {
            latchAndZero();
            setBanner("Sortie native LAND/RTL en cours — commandes de déplacement verrouillées");
          }
          nativeExitRef.current = m.native_exit_pending === true;
          lastAgentStateRef.current = m.state;
          if (m.timing && trace?.collector) {
            try { trace.collector.ingestStatus(m.timing, performance.timeOrigin + performance.now()); } catch { /* observation seule */ }
          }
          if (phaseRef.current === "granted") {
            const p = m.preconditions || {};
            const er = m.state === "READY" && m.px4_mode === "POSCTL" && p.scope === "entry"
              && p.allowed === true && Array.isArray(p.reasons) && p.reasons.length === 0;
            setPrecheck((n) => (er ? Math.min(PRECHECK_REQUIRED, n + 1) : 0));
          }
          if ((m.state === "PILOT_OVERRIDE" || m.pilot_override === true)
              && phaseRef.current !== "idle" && phaseRef.current !== "takeover") {
            stopStream(); zeroLocal(); moveAllowedRef.current = false;
            setPhase("takeover"); setBanner("Contrôle repris par le pilote");
            return;
          }
          // PILOT.BLOCKERS.1 — LAND/RTL : le flux neutre ne s'arrête qu'à la sortie native CONFIRMÉE par le statut.
          // R1 : un mode absent/null/vide ne confirme rien ; session active => cible nulle + flux neutre conservés.
          if (critRef.current && ["active", "active_hold"].includes(phaseRef.current)) {
            const mode = typeof m.px4_mode === "string" ? m.px4_mode.trim().toUpperCase() : "";
            const T = String(critRef.current.type).toUpperCase();
            if ((NATIVE_EXIT_MODES[critRef.current.type] || []).includes(mode)) {
              stopStream(); zeroLocal(); moveAllowedRef.current = false; rearmPendingRef.current = false;
              setPhase("granted");
              setBanner(`${T} : sortie native confirmée (mode=${m.px4_mode}) — flux OFFBOARD arrêté`);
              return;
            }
            if (mode && mode !== "OFFBOARD") {   // R2 : sortie vers un AUTRE mode -> jamais annoncée comme succès
              stopStream(); zeroLocal(); moveAllowedRef.current = false; rearmPendingRef.current = false;
              setPhase("granted");
              setBanner(`Sortie OFFBOARD inattendue pendant ${T} (mode réel=${m.px4_mode}) — ${T} non confirmé, axes verrouillés`);
              return;
            }
            if (!mode && AUTHORITY_STATES.includes(m.state)) { zeroLocal(); return; } // mode inconnu : on attend
            // sinon : véritable perte d'autorité -> branche authorityLostDuringActive ci-dessous.
          }
          // Perte d'autorité pendant active/active_hold (OFFBOARD/ACTIVE tombé, hors PILOT_OVERRIDE) :
          // verrouillage immédiat, cible zéro, manual_stop best-effort, AUCUNE réacquisition auto.
          if (authorityLostDuringActive({ phase: phaseRef.current, status: m })) {
            stopStream(); zeroLocal(); moveAllowedRef.current = false;
            if (sidRef.current) send(buildManualStop(sidRef.current));
            setPhase("granted");
            setBanner(`Autorité OFFBOARD perdue (state=${m.state}, mode=${m.px4_mode}) — mouvement verrouillé`);
            return;
          }
          if (m.state === "ACTIVE" && m.px4_mode === "OFFBOARD" && phaseRef.current === "offboard_starting") {
            activeAtRef.current = Date.now(); setPhase("active_hold");
            setTimeout(() => {
              if (phaseRef.current === "active_hold") { moveAllowedRef.current = true; setPhase("active"); }
            }, REAL_ACTIVE_HOLD_MS);
          }
          if (["READY", "DISABLED", "FAILED"].includes(m.state) && phaseRef.current === "exiting") {
            stopStream(); setPhase("granted");
          }
          if (rearmPendingRef.current && canRearmMovement({
            cockpitFocused: focusedRef.current,
            inputsNeutral: heldDirsRef.current.size === 0 && !analogRef.current.active && !holdLatchRef.current.analog,
            freshStatus: statusCountRef.current > rearmAtStatusRef.current,
            status: m, phase: phaseRef.current, leaseGranted: !!sidRef.current,
            wsOpen: wsRef.current?.readyState === 1,
            orchMovementAllowed: orchRef.current?.movement_allowed === true,
            criticalAction: critRef.current,
          })) {
            rearmPendingRef.current = false; moveAllowedRef.current = true;
            setBanner("Commandes réautorisées — nouvelle action du pilote requise");
          }
        } else if (m.type === "control_lost") {
          setLastError({ error: m.reason || "control_lost", at: Date.now() });
        } else if (m.type === "offboard_error") {
          setLastError({ error: m.error || "offboard_error", at: Date.now() });
          if (trace?.collector) trace.collector.ingestError(m);
          toast.error(`Offboard: ${m.error || "erreur"}`);
        } else if (m.type === "orchestration_status") {
          setOrch(m); orchRef.current = m;
          if (m.phase === "STABILIZING" && !["offboard_starting", "active_hold", "active"].includes(phaseRef.current)) {
            // A.5.4H.4F.3.2 : UNE connexion/lease = UN compteur seq strictement monotone.
            // AUCUN reset de seqRef entre deux vols sur la même connexion (il n'augmente que).
            zeroLocal(); moveAllowedRef.current = false;
            stopStream(); setPhase("stabilizing");
          }
          if (["PRESTREAM", "OFFBOARD_ENTERING"].includes(m.phase) && !["active_hold", "active"].includes(phaseRef.current)) {
            zeroLocal(); moveAllowedRef.current = false;
            setPhase("offboard_starting"); startStream();
          }
          if (m.phase === "PILOT_OVERRIDE") {
            stopStream(); zeroLocal(); moveAllowedRef.current = false;
            setPhase("takeover"); setBanner("Contrôle repris par le pilote");
          }
          if (["OFFBOARD_NOT_ACQUIRED", "FAILED", "CANCELLED"].includes(m.phase)) {
            stopStream(); zeroLocal(); moveAllowedRef.current = false;
            setPhase("granted");
            setBanner(`Vol auto: ${m.reason || m.phase}`);
          }
        } else if (m.type === "orchestration_error") {
          toast.error(`Vol auto: ${m.error || "erreur"}`);
          setPhase("granted");
          setBanner(`Démarrage refusé: ${m.error || ""}`);
        } else if (m.type === "offboard_unavailable") {
          // WEBXR.REAL.CONTROL.1 A1 — link_lost est TERMINAL (aucune transition de retour) : on ne garde
          // pas une connexion/session « ouverte » dont le lease expirerait en silence. Neutralisation locale
          // IMMÉDIATE, autorité locale invalidée, manual_stop best-effort, fermeture => le Backend libère le
          // lease (jeton exact). Côté drone : input_timeout Agent / HOLD inchangés. Aucune reprise auto :
          // nouvelle prise de contrôle explicite requise. LAND/RTL restent disponibles hors lease.
          stopStream(); stopStatusPoll(); latchAndZero(); moveAllowedRef.current = false;
          phaseRef.current = "link_lost"; setPhase("link_lost");
          setBanner(`Drone indisponible: ${m.reason || ""} — contrôle libéré, reprise manuelle requise`);
          setLastError({ error: m.reason || "offboard_unavailable", at: Date.now() });
          const lostSid = sidRef.current;
          sidRef.current = null; setGranted(null);
          try { if (lostSid && ws.readyState === 1) ws.send(JSON.stringify(buildManualStop(lostSid))); } catch { /* best-effort */ }
          wsRef.current = null;  // connexion abandonnée : plus aucun message traité ni envoyé, même si close échoue
          setConn("closed");
          try { ws.close(1000, "offboard_unavailable"); } catch { /* best-effort : TTL serveur en dernier recours */ }
        }
      };
    } catch (e) { setConn("closed"); toast.error(apiErr(e, "Connexion WS impossible")); }
  }, [droneId, send, startStream, stopStream, zeroLocal, latchAndZero, startStatusPoll, stopStatusPoll, trace]);

  // Action opérateur UNIQUE : ARM -> TAKEOFF -> AUTO OFFBOARD. Gardes DANS startFlight (pas seulement le disabled).
  const startFlight = useCallback((alt) => {
    if (critRef.current) { toast.error("Action critique en cours"); return; }
    if (conn !== "open") { toast.error("WebSocket non connecté"); return; }
    if (!(granted && granted.session_id)) { toast.error("Lease de contrôle absente"); return; }
    if (!realEnabled) { toast.error("Contrôle réel indisponible (autorité Raspberry requise)"); return; }
    // MATRIX.5 — gate d'identité + capacités OFFBOARD (garde UX ; l'Agent reste l'autorité fail-closed).
    const fcBlock = offboardCapabilityBlocked(flightController);
    if (fcBlock) { toast.error("OFFBOARD indisponible : " + fcBlock); return; }
    // UNIFIED.2 — garde précheck sol autoritaire : aucun message de démarrage si preflight absent/refusé.
    if (!preflight || preflight.allowed !== true) {
      const why = preflight?.reasons?.length ? preflight.reasons.join(", ") : "préchecks sol non reçus";
      toast.error("Préflight sol non validé — ARM+TAKEOFF bloqué (" + why + ")");
      return;
    }
    const safety = authoritativeSafety(status);
    if (safety.armed !== false) { toast.error("Déjà armé — ARM+TAKEOFF impossible"); return; }
    if (safety.landed_state !== 1) { toast.error("Drone pas confirmé au sol"); return; }
    // UNIFIED.3A — revalidation altitude EN PROFONDEUR : les 2 politiques (Backend+Agent) doivent être
    // présentes, identiques, et l'altitude valide. Sinon aucun auto_takeoff_offboard_start.
    const _ag = validateTakeoffPolicy(status?.takeoff_policy);
    const _ready = !!(backendTakeoffPolicy && _ag && backendTakeoffPolicy.min === _ag.min && backendTakeoffPolicy.max === _ag.max);
    const _av = validateTakeoffAltitude(alt, _ready ? _ag : null);
    if (!_ready || !_av.valid) {
      toast.error(!_ready ? "Politique TAKEOFF Backend/Agent manquante ou incohérente" : "Altitude TAKEOFF hors politique autorisée");
      return;
    }
    // A.5.4H.4F.3.2 : pas de reset seqRef au démarrage d'un vol (compteur monotone par connexion/lease).
    setBanner(null); zeroLocal(); moveAllowedRef.current = false;
    send(buildTakeoffStart(alt));
    setPhase("offboard_starting");
  }, [conn, granted, realEnabled, status, send, zeroLocal, flightController, preflight, backendTakeoffPolicy]);

  const stopMove = useCallback(() => {
    zeroLocal();
    if (sidRef.current) send(buildManualStop(sidRef.current));
  }, [send, zeroLocal]);

  // HOVER réel : consigne zéro CONTRÔLÉE sur les 4 axes, le stream 10 Hz continue (deadman=true).
  // NE PAS envoyer manual_stop (éviterait un passage ACTIVE->HOLDING bloquant la reprise).
  const hover = useCallback(() => {
    heldDirsRef.current.clear();
    analogRef.current = { pitch: 0, roll: 0, lift: 0, yaw: 0, active: false };
    targetRef.current = zeroTarget();
    setActiveDirs(new Set());
    if (curDirRef.current !== null) { curDirRef.current = null; }
    inputKindRef.current = "zero_keepalive";
    holdLatchRef.current = holdLatchFrom(); // neutre explicite opérateur : verrou levé
  }, []);

  // UNIFIED.7 : exitRtl (manual_exit opérateur) retiré du frontend. Le RTL opérateur canonique est
  // real.rtlReal() (commande RTL native + command_id + lifecycle). manual_exit reste un mécanisme
  // interne/safety côté Agent/Backend, mais n'est plus exposé comme commande opérateur frontend.
  const updateCritical = useCallback((patch) => {
    const next = { ...(critRef.current || {}), ...patch };
    critRef.current = next; setCriticalAction(next);
  }, []);
  const clearCritical = useCallback((bannerMsg) => {
    if (critPollRef.current) { clearInterval(critPollRef.current); critPollRef.current = null; }
    critRef.current = null; setCriticalAction(null);
    if (bannerMsg) setBanner(bannerMsg);
  }, []);

  // Suivi READ-ONLY 1 Hz par command_id. JAMAIS de POST ici.
  const startCritPoll = useCallback((type, commandId) => {
    if (critPollRef.current) clearInterval(critPollRef.current);
    critPollRef.current = setInterval(async () => {
      try {
        const doc = await fetchCommand(api, droneId, commandId);
        const s = doc?.status;
        updateCritical({ status: s, uncertain: isUncertainStatus(s) });
        if (isCriticalFailStatus(s)) { clearCritical(`${type.toUpperCase()}: échec (${s}) — aucune nouvelle commande envoyée`); return; }
        if (isUncertainStatus(s)) { updateCritical({ note: `Résultat ${type.toUpperCase()} incertain — aucune nouvelle commande envoyée` }); return; } // reste verrouillé, GET continue
        if (s === "executed") {
          if (type === "land") clearCritical("LAND exécuté — posé, désarmé");
          else { updateCritical({ note: "RTL confirmé — attente atterrissage (télémétrie)" });
                 if (critPollRef.current) { clearInterval(critPollRef.current); critPollRef.current = null; } }
        }
      } catch { /* GET échoué : on retente au prochain tick, jamais de POST */ }
    }, 1000);
  }, [droneId, updateCritical, clearCritical]);

  // LAND / RTL réels : UN command_id client, POST idempotent (X-Command-Id) UNE fois, suivi read-only. Aucun retry auto.
  const runCritical = useCallback((type) => {
    if (!canStartCritical(critRef.current)) return; // verrou : aucun 2e clic critique
    if (conn !== "open" || !realEnabled || status?.source !== "raspberry") { toast.error(`${type.toUpperCase()} indisponible (autorité requise)`); return; }
    const safety = authoritativeSafety(status);
    if (safety.armed !== true || safety.landed_state === 1) { toast.error(`${type.toUpperCase()} impossible au sol`); return; }
    const commandId = newClientCommandId();
    critRef.current = { type, commandId, status: "sending", uncertain: false, note: `${type.toUpperCase()} en cours…` };
    setCriticalAction(critRef.current);
    // PILOT.BLOCKERS.1 : cible nulle + verrou immédiats, flux neutre 10 Hz MAINTENU jusqu'à la sortie
    // native confirmée par le statut Raspberry/PX4 (une réponse HTTP sent/accepted ne suffit pas).
    latchAndZero(); armRearm();
    setBanner(`${type.toUpperCase()} en cours…`);
    (async () => {
      try {
        const doc = await postCriticalCommand(api, droneId, type, commandId);
        updateCritical({ status: doc?.status || "sent" });
      } catch (e) {
        // Erreur réseau : NE PAS supposer non créée. Rechercher via GET avec le MÊME command_id. Aucun retry POST.
        updateCritical({ note: "Transmission non confirmée — recherche via GET…" });
        try {
          const doc = await fetchCommand(api, droneId, commandId);
          updateCritical({ status: doc?.status || "sent" });
        } catch {
          updateCritical({ status: "unconfirmed", note: "Transmission non confirmée — vérifier l'état du drone avant toute nouvelle commande" });
        }
      }
      startCritPoll(type, commandId);
    })();
  }, [conn, realEnabled, status, droneId, latchAndZero, armRearm, updateCritical, startCritPoll]);

  const landReal = useCallback(() => runCritical("land"), [runCritical]);
  const rtlReal = useCallback(() => runCritical("rtl"), [runCritical]);

  const markInput = useCallback((kind) => {
    inputEventIdRef.current += 1; inputKindRef.current = kind;
    const perf = performance.now();
    inputChangedRef.current = { perf, epoch: performance.timeOrigin + perf };
  }, []);

  // Bouton/clavier : maintien d'un axe. Le relâchement remet CET axe à zéro (les autres axes actifs restent).
  const press = useCallback((dir) => {
    if (!moveAllowed) return;
    if (!isRealDirection(dir)) return;
    if (holdLatchRef.current.dirs.has(dir)) return; // ancien bouton encore tenu : relâcher d'abord
    heldDirsRef.current.add(dir);
    recomputeTarget();
    setActiveDirs(new Set(heldDirsRef.current));
    if (curDirRef.current !== dir) { curDirRef.current = dir; markInput("move"); }
  }, [moveAllowed, markInput, recomputeTarget]);

  const release = useCallback((dir) => {
    if (dir && isRealDirection(dir)) { heldDirsRef.current.delete(dir); holdLatchRef.current.dirs.delete(dir); }
    else { heldDirsRef.current.clear(); holdLatchRef.current.dirs.clear(); }
    recomputeTarget();
    setActiveDirs(new Set(heldDirsRef.current));
    if (heldDirsRef.current.size === 0 && !analogRef.current.active) {
      if (curDirRef.current !== null) { curDirRef.current = null; markInput("release"); }
    }
  }, [markInput, recomputeTarget]);

  // Entrée analogique gamepad (pitch->vx, roll->vy, lift->vz, yaw->yaw_rate). Deadzone par axe -> 0.
  const setAnalog = useCallback(({ pitch = 0, roll = 0, lift = 0, yaw = 0 } = {}) => {
    // Stocke l'entrée NORMALISÉE (-1..1) avec deadzone ; la vitesse est recalculée avec le profil courant.
    const dz = (v) => (Math.abs(v) < 0.08 ? 0 : v);
    const p = dz(pitch), r = dz(roll), l = dz(lift), y = dz(yaw);
    const active = !!(p || r || l || y);
    // Axe tenu pendant un verrou (HOLDING / safetyZero / mouvement interdit) : neutre requis avant reprise.
    if (holdLatchRef.current.analog || !moveAllowed) {
      holdLatchRef.current.analog = active;
      if (active || !moveAllowed) return;
    }
    const was = analogRef.current.active;
    if (active) analogAtRef.current = Date.now();
    analogRef.current = { pitch: p, roll: r, lift: l, yaw: y, active };
    recomputeTarget();
    if (active && curDirRef.current !== "analog") { curDirRef.current = "analog"; markInput("move"); }
    else if (!active && was && heldDirsRef.current.size === 0) { curDirRef.current = null; markInput("release"); }
  }, [moveAllowed, markInput, recomputeTarget]);

  // UNIFIED.3 — API vitesse (réutilisable par Gamepad / futur Quest WebXR via les mêmes primitives).
  const changeSpeedProfile = useCallback((profile) => {
    if (!["slow", "normal", "rapid"].includes(profile)) return;
    setSpeedProfile(profile);
  }, []);
  const speedUp = useCallback(() => setSpeedProfile((p) => nextSpeedProfile(p)), []);
  const speedDown = useCallback(() => setSpeedProfile((p) => prevSpeedProfile(p)), []);
  // Recalcule immédiatement la cible quand le profil change (axe maintenu : vitesse mise à jour sans relâcher).
  useEffect(() => { recomputeTarget(); }, [speedProfile, controlLimits, recomputeTarget]); // eslint-disable-line

  // Sécurités navigateur (deadman) — actives UNIQUEMENT en mode réel. La translation clavier de PILOTAGE
  // est désormais gérée par usePilotInput (COCKPIT.FINAL.7) ; ces sécurités restent ici, autoritaires.
  useEffect(() => {
    if (!enabled) return undefined;
    const onBlur = () => safetyZero("Focus perdu — vitesse remise à zéro", { focusLost: true });
    // Retour du cockpit : la réautorisation attend un NOUVEAU statut Raspberry (jamais immédiate).
    const onBack = () => {
      if (document.visibilityState === "hidden") return;
      focusedRef.current = true; rearmAtStatusRef.current = statusCountRef.current;
    };
    const onVis = () => {
      if (document.visibilityState === "hidden") safetyZero("Onglet masqué — vitesse remise à zéro", { focusLost: true });
      else onBack();
    };
    const onPageHide = () => safetyZero("Page masquée — vitesse remise à zéro", { focusLost: true });
    // pointercancel (perte anormale du pointeur) : sécurité complète.
    const onPointerCancel = () => safetyZero("Pointeur perdu — vitesse remise à zéro");
    const onGamepadLost = () => safetyZero("Manette déconnectée — vitesse remise à zéro");
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onBack);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pointercancel", onPointerCancel);
    window.addEventListener("gamepaddisconnected", onGamepadLost);
    return () => {
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onBack);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pointercancel", onPointerCancel);
      window.removeEventListener("gamepaddisconnected", onGamepadLost);
    };
  }, [enabled, safetyZero]);

  // Déblocage RTL : RTL executed = mode confirmé seulement ; on attend la télémétrie posée (landed_state=1 & armed=false).
  useEffect(() => {
    const safety = authoritativeSafety(status);
    if (criticalAction?.type === "rtl" && criticalAction?.status === "executed"
        && criticalUnlockReady({ type: "rtl", status: "executed", armed: safety.armed, landed_state: safety.landed_state })) {
      clearCritical("RTL terminé — drone posé, désarmé");
    }
  }, [status, criticalAction, clearCritical]);

  // WEBXR.REAL.CONTROL.1 — libération VOLONTAIRE du contrôle : zéro + manual_stop + fermeture WS (lease libérée
  // côté Backend à la déconnexion). Aucune commande LAND/RTL émise.
  const disconnect = useCallback(() => {
    stopStream(); stopStatusPoll(); zeroLocal(); moveAllowedRef.current = false;
    phaseRef.current = "idle"; setPhase("idle");
    try { if (sidRef.current && wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify(buildManualStop(sidRef.current))); } catch { /* noop */ }
    sidRef.current = null; setGranted(null); setStatus(null); setTransport(null);
    try { wsRef.current?.close(); } catch { /* noop */ }
    wsRef.current = null;
  }, [stopStream, stopStatusPoll, zeroLocal]);

  // Démontage / désactivation : zéro + manual_stop + fermeture WS + arrêt du suivi critique.
  useEffect(() => () => {
    stopStream(); stopStatusPoll();
    if (critPollRef.current) { clearInterval(critPollRef.current); critPollRef.current = null; }
    try { if (sidRef.current && wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify(buildManualStop(sidRef.current))); } catch { /* noop */ }
    try { wsRef.current?.close(); } catch { /* noop */ }
  }, [stopStream, stopStatusPoll]);

  return {
    conn, status, granted, transport, phase, precheck, orch, activeDirs, banner, setBanner,
    realEnabled, entryReady, isActive, moveAllowed, precheckRequired: PRECHECK_REQUIRED, criticalAction,
    preflight, canStartFlight: canStartFlightNow,
    speedProfile, setSpeedProfile: changeSpeedProfile, speedUp, speedDown,
    controlLimits, controlLimitsReady, profileLimits,
    takeoffPolicy, takeoffPolicyReady, backendTakeoffPolicy, agentTakeoffPolicy, takeoffPolicyMismatch,
    safety: authoritativeSafety(status),
    connect, disconnect, startFlight, stopMove, hover, landReal, rtlReal, lastError,
    press, release, setAnalog, safetyZero,
    targetRef, sessionId: granted?.session_id || null,
  };
}
